import useResponsive from "src/hooks/useResponsive";
import useCollapseDrawer from "src/hooks/useCollapseDrawer";
import cssStyles from "src/utils/cssStyles";
import Logo from "src/components/Logo";
import Scrollbar from "src/components/Scrollbar";
import CollapseButton from "./CollapseButton";
import NavbarAccount from "./NavbarAccount";
import useAuth from "src/hooks/useAuth";
import getMenuNavigation from "./NavConfig";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { styled, useTheme } from "@mui/material/styles";
import { Box, Stack, Drawer, CircularProgress } from "@mui/material";
import { NAVBAR } from "src/config/config";
import { NavSectionVertical } from "src/components/nav-section";
import { useProfileUserPermissions } from "src/modules/profile/hooks/Profile/useUserPermissions";
import { useTranslation } from "react-i18next";
import { useProfileUserDetails } from "src/modules/profile/hooks/Profile/useUserDetails";
import { useCompanyDetails } from "src/modules/company/hooks/useCompanyDetails";

const RootStyle = styled("div")(({ theme }) => ({
    [theme.breakpoints.up("lg")]: {
        flexShrink: 0,
        "& .collapse-btn": {
            opacity: 0,
            visibility: "hidden",
            transition:
                "opacity 300ms cubic-bezier(0.4, 0, 0.2, 1) 500ms, visibility 300ms cubic-bezier(0.4, 0, 0.2, 1) 500ms",
        },
        "&:hover .collapse-btn": {
            opacity: 1,
            visibility: "visible",
            transition:
                "opacity 300ms cubic-bezier(0.4, 0, 0.2, 1), visibility 300ms cubic-bezier(0.4, 0, 0.2, 1)",
        },
    },
}));

type Props = {
    isOpenSidebar: boolean;
    onCloseSidebar: VoidFunction;
};

export default function NavbarVertical({ isOpenSidebar, onCloseSidebar }: Props) {
    const isDesktop = useResponsive("up", "lg");
    const theme = useTheme();
    const { t } = useTranslation();
    const { userState } = useAuth();
    const { pathname } = useLocation();
    const { isCollapse, collapseClick, collapseHover, onToggleCollapse } = useCollapseDrawer();

    const { data: permissions } = useProfileUserPermissions();
    const isMenuLocked = permissions?.isCompanySubscriptionActive === false;

    const { data: companyData } = useCompanyDetails();
    const hasDefaultTariff = companyData && companyData.hasDefaultTariff;

    const { data: profile, refetch } = useProfileUserDetails();

    useEffect(() => {
        if (isOpenSidebar) {
            onCloseSidebar();
        }
    }, [pathname]);

    useEffect(() => {
        if (!profile) refetch();
    }, []);

    const renderContent = (
        <Scrollbar
            sx={{
                height: 1,
                "& .simplebar-content": { height: 1, display: "flex", flexDirection: "column" },
            }}
        >
            <Stack
                spacing={3}
                sx={{
                    pt: isCollapse ? 2.25 : 3,
                    pb: 2,
                    px: 2.5,
                    flexShrink: 0,
                    ...(isCollapse && { alignItems: "center" }),
                }}
            >
                <Logo isCollapse={isCollapse} />
                {!isCollapse && permissions && profile && userState && (
                    <NavbarAccount
                        isCollapse={isCollapse}
                        role={
                            permissions.hasFullAccess
                                ? t("shared.permissions.administrator")
                                : t("shared.permissions.operator")
                        }
                        company={userState?.company?.name}
                    />
                )}
            </Stack>

            {permissions ? (
                <Box
                    sx={{
                        pointerEvents: isMenuLocked ? "none" : "auto",
                        opacity: isMenuLocked ? 0.6 : 1,
                    }}
                >
                    <NavSectionVertical
                        navConfig={getMenuNavigation(permissions, !!hasDefaultTariff)}
                        isCollapse={isCollapse}
                    />
                </Box>
            ) : (
                <Box ml={4} mt={4}>
                    <CircularProgress />
                </Box>
            )}

            <Box sx={{ flexGrow: 1, paddingBottom: 8 }} />
        </Scrollbar>
    );

    return (
        <RootStyle
            sx={{
                width: {
                    lg: isCollapse ? NAVBAR.DASHBOARD_COLLAPSE_WIDTH : NAVBAR.DASHBOARD_WIDTH,
                },
                ...(collapseClick && {
                    position: "absolute",
                }),
            }}
        >
            {!isDesktop && (
                <Drawer
                    open={isOpenSidebar}
                    onClose={onCloseSidebar}
                    PaperProps={{ sx: { width: NAVBAR.DASHBOARD_WIDTH } }}
                >
                    {renderContent}
                </Drawer>
            )}
            {isDesktop && (
                <>
                    <Box
                        className="collapse-btn"
                        sx={{
                            backgroundColor: "background.default",
                            border: "1px solid",
                            borderColor: "divider",
                            width: 26,
                            height: 26,
                            borderRadius: "50%",
                            position: "fixed",
                            top: isCollapse ? 16 : 27,
                            left: isCollapse
                                ? NAVBAR.DASHBOARD_COLLAPSE_WIDTH - 13
                                : NAVBAR.DASHBOARD_WIDTH - 13,
                            zIndex: 10000,
                        }}
                    >
                        <CollapseButton
                            onToggleCollapse={onToggleCollapse}
                            collapseClick={collapseClick}
                        />
                    </Box>
                    <Drawer
                        open
                        variant="persistent"
                        PaperProps={{
                            sx: {
                                width: NAVBAR.DASHBOARD_WIDTH,
                                bgcolor: "background.default",
                                // transition: (theme) =>
                                //     theme.transitions.create("width", {
                                //         duration: theme.transitions.duration.standard,
                                //     }),
                                ...(isCollapse && {
                                    width: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
                                }),
                                ...(collapseHover && {
                                    ...cssStyles(theme).bgBlur(),
                                    boxShadow: (theme) => theme.customShadows.z24,
                                }),
                            },
                        }}
                    >
                        {renderContent}
                    </Drawer>
                </>
            )}
        </RootStyle>
    );
}
